import gsap from 'gsap';
import { CustomEase } from 'gsap/CustomEase';
import { get, set, setAudioState } from './SCORM';
import safeLocalStorage from './safeLocalStorage';
import { disableSfx, enableSfx } from './sfx';
import getQueryParameter from './getQueryParameter';
import { storeToRefs } from 'pinia';
import useContent from '../stores/useContent';

interface PlayAudioOptions {
  duration?: number;
  delay?: number;
  loop?: boolean;
  persist?: boolean;
  volume?: number;
  playbackRate?: number;
  fadeIn?: number;
  owner?: string | null;
}

gsap.registerPlugin(CustomEase);

// whether is audio enabled in this course
let isEnabled = true;

// the currently "active" audio (playing, about to be played, or muted)
let activeId: string | null = null;
let activeOwner: string | null = null;

let persist: boolean = false;
let volume: number = Number(safeLocalStorage.getItem('uc-audio-volume') ?? 1);
let playbackRate: number = Number(
  safeLocalStorage.getItem('uc-audio-playbackrate') ?? 1
);

// object to store audio elements
const fadeOut = 0.5;
const fadeIn = 0;

// Check if audio state should be set
// This is only used to lock progress until audio is finished playing
// Only on courses with content.value.course.audiolocked

function setAudioStateIfLocked(audioplaying: boolean) {
  try {
    const { compiled } = storeToRefs(useContent());
    const content = compiled;

    if (content?.value.course?.audiolocked) {
      setAudioState(audioplaying);
    }
  } catch (error) {
    console.error(error);
  }
}

// a single audio element which we use to play ALL narration audio by changing it's src prop (better for iOS autoplay permission)
const playbackFile = new Audio();
playbackFile.addEventListener('ended', function () {
  setAudioStateIfLocked(false);
  playNextInQueue();
});

const audioDoc = Boolean(getQueryParameter('audioDoc'));
const wordDoc = Boolean(getQueryParameter('word'));

// mute if saved in suspend data
if (get('mute') || get('narrationMute')) {
  mute();
}

if (!volume) {
  setVolume(1);
}

if (!playbackRate) {
  setPlaybackRate(1);
}

let queued: { id: string; options: PlayAudioOptions }[] = [];

// custom ease for fading out audio
CustomEase.create('audioEaseOut', 'M0,0 C0,0.9 0.1,1 1,1');

export function setVolume(vol: number) {
  volume = vol;
  safeLocalStorage.setItem('uc-audio-volume', String(volume));

  if (playbackFile) playbackFile.volume = vol;
}

export function getVolume() {
  return Number(volume ?? 1);
}

export function setPlaybackRate(rate: number) {
  playbackRate = rate;
  safeLocalStorage.setItem('uc-audio-playbackrate', String(playbackRate));

  if (playbackFile) playbackFile.playbackRate = rate;
}

export function getPlaybackRate() {
  return Number(playbackRate ?? 1);
}

export function nowPlaying() {
  return activeId;
}

export function currentAudioOwner() {
  return activeOwner;
}

export function isPlaying(id: string) {
  return activeId === id;
}

export function playNextInQueue() {
  if (queued.length === 0) return;

  const next = queued.shift();
  if (next) {
    setAudioStateIfLocked(false);
    play(next.id, next.options);
  }
}

export function playSilence() {
  play(
    'https://media2.vinciworks.com/wp-content/uploads/kitchen-sink/audio/1-second-silence.mp3'
  );
}

function queue(id: string, options: PlayAudioOptions = {}) {
  // Check if the audio is already in the queue and return if true
  if (queued.some(q => q.id === id)) return;

  console.log('queue', id);
  queued.push({ id, options });
}

/**
 * Play the first non-empty URL and queue the rest in order. Shared by the
 * container templates (carousel, slides, split, uc-screen templates chain) that
 * resolve several clips — parent intro + nested templates[]/split panels — and
 * need to play the first immediately so the queue kicks off, then follow on.
 */
export function playUrlSequence(
  urls: (string | null | undefined)[],
  options: { owner?: string | null; delay?: number } = {}
) {
  const flat = urls.filter((u): u is string => Boolean(u));
  if (!flat.length) return;
  play(flat[0], { owner: options.owner, delay: options.delay });
  for (let j = 1; j < flat.length; j++)
    queue(flat[j], { owner: options.owner });
}

/**
 * Discards all pending queued items without stopping the currently playing audio.
 * Use before a manual play call when you want to cancel any auto-queued sequence
 * (e.g. a timeline's initial step sequence) so the manually-triggered audio
 * doesn't fall back into the stale queue after it finishes.
 */
export function clearQueue() {
  queued = [];
}

/**
 * Returns a Promise that resolves the next time the playback element fires its
 * 'ended' event (i.e. the currently playing audio track finishes naturally).
 *
 * Resolves immediately if nothing is currently playing, so callers don't need
 * to guard against the no-audio case themselves.
 *
 * Usage: await whenCurrentEnds() before gating behaviour on audio completion.
 */
export function whenCurrentEnds(): Promise<void> {
  if (!activeId) return Promise.resolve();
  return new Promise<void>(resolve => {
    playbackFile.addEventListener('ended', () => resolve(), { once: true });
  });
}

/**
 * Plays an audio file.
 *
 * The paratemeter `id` is a string representing the path to the audio in the
 * `./src/audio` directory, without the '.mp3' extension. For example, the
 * `id` for `./src/audio/narrator/intro.mp3` is `'narrator/intro'`.
 *
 * The active audio variable `activeId` is set to this ID.
 *
 * If audio has not been muted, then the element plays with `volume` 1 and `currentTime` 0.
 *
 * @param {string} id - the ID of the audio
 * @param {object} options
 * @param {number} options.duration Duration of volume transition, in seconds (default 0)
 * @param {number} options.delay Delay, in seconds (default 0)
 * @param {boolean} options.loop Loop audio file (default false)
 * @param {boolean} options.persist [TODO] Don't stop on navigation (default false)
 * @param {number} options.volume Set volume for this file when playing and resuming (default 1)
 * @param {number} options.playbackRate Set playbackRate for this file when playing and resuming (default 1)
 */
async function play(
  id: string,
  {
    fadeIn: dur = fadeIn,
    delay = 0,
    loop = false,
    persist: _persist = persist,
    volume: vol = getVolume(),
    playbackRate: rate = getPlaybackRate(),
    owner
  }: PlayAudioOptions = {}
) {
  if (!isEnabled || audioDoc || wordDoc) {
    return false;
  }

  if (!id) {
    throw new Error('No id to play');
  }

  persist = _persist;

  // is this audio already active?
  if (id === activeId) {
    resume();

    console.log('resume', id);

    return true;
  }

  // check if in queue and remove should already have been removed by playNextInQueue
  queued = queued.filter(q => q.id !== id);

  // stop any active audio if the queue is empty, ready for playing the new file
  if (queued.length === 0) {
    await stop({ fadeOut: Math.max(delay * 0.5, 0.25) });
  }

  // set active ID
  activeId = id;
  activeOwner = owner ?? null;
  document.dispatchEvent(
    new CustomEvent('activeAudioChanged', { detail: { activeId: id } })
  );

  // if silent, stop here
  if (isSilent()) {
    return true;
  } else {
    console.log('play', id);
  }

  // check is we have been silenced or active audio has changed
  if (!isSilent() && id === activeId) {
    playbackFile.src = id; // it's important to set the src first so the following props apply
    playbackFile.currentTime = 0;
    playbackFile.volume = 0;
    playbackFile.playbackRate = rate;
    playbackFile.loop = Boolean(loop);
    playbackFile.preload = 'auto';

    // if the audio is ready to play, add a short delay (for fast scrolling etc.)
    if (playbackFile.readyState === HTMLMediaElement.HAVE_ENOUGH_DATA) {
      await sleep(delay);
    }

    //  start to load then play the requested audio file
    const startPlayPromise = playbackFile.play();

    /**
     * Start 5 second timer to see if we loaded and started playing the file
     * If not, try the queue
     */
    let timeout = setTimeout(() => {
      console.log(
        'Audio slow timeout',
        playbackFile.readyState,
        playbackFile.src
      );

      if (queued.length > 0) {
        console.log('Trying queue');
        playNextInQueue();
      } else {
        stop();
      }

      clearTimeout(timeout);
    }, 5000);

    if (startPlayPromise !== undefined) {
      startPlayPromise
        .then(() => {
          console.log('playing', id);
          clearTimeout(timeout);
        })
        .catch(error => {
          clearTimeout(timeout);

          // permission error (often Safari on iOS)
          // we mute so that unmuting forces interaction which then allows permission
          if (error.name === 'NotAllowedError') {
            console.log('Audio not allowed to play', playbackFile.src);
            mute();
          } else if (error.name === 'AbortError') {
            // A new src/load request superseded this play() call before it could
            // start. This is expected when audio switches rapidly (e.g. scroll
            // triggers firing in quick succession). Silently discard — the new
            // play() call is already in progress and the queue should not be
            // advanced here.
          } else {
            // an audio error is often just a screen without audio, so we need to try to play the queued items, if there are any.
            console.error(
              `Audio play error (${error.name}): ${playbackFile.src}.`
            );

            if (queued.length > 0) {
              console.log('Trying queue');
              playNextInQueue();
            } else {
              stop();
            }

            return false;
          }
        });
    }

    return new Promise(resolve => {
      gsap.to(playbackFile, {
        volume: vol,
        ease: 'audioEaseOut',
        overwrite: true,
        duration: dur / rate,
        onComplete() {
          if (id === activeId) {
            setAudioStateIfLocked(true);
          }
          resolve(true);
        }
      });
    });
  }

  return true;
}

/**
 * Stops any currently playing audio.
 *
 * Sets the active audio variable `activeId` to `null`.
 *
 * If an audio element is currently playing, its `volume` tweens to 0 and then is set to paused and `currentTime` of 0.
 */
function stop({ fadeOut: dur = fadeOut } = {}) {
  if (!isEnabled) {
    return false;
  }

  if (!activeId) {
    activeOwner = null;
    return true;
  }

  // clear current audio
  activeId = null;
  activeOwner = null;
  queued = [];

  return new Promise(resolve => {
    gsap.to(playbackFile, {
      volume: 0,
      duration: dur,
      onComplete() {
        playbackFile.src = '';
        playbackFile.currentTime = 0;
        setAudioStateIfLocked(false);
        resolve(true);
      },
      ease: 'audioEaseOut',
      overwrite: true
    });

    console.log('stop');
  });
}

/**
 * Pauses audio. For use by mute().
 *
 * Does not modify the active audio.
 *
 * If an audio element is currently playing, its `volume` tweens to 0 and then is set to paused. The duration of the tween is subtracted from `currentTime`, but only if the current time was not too close to the beginning or end
 */
function pause() {
  if (!isEnabled) {
    return false;
  }

  // no active audio
  if (!activeId) {
    return true;
  }

  const dur = 0.8;

  return new Promise(resolve => {
    gsap.to(playbackFile, {
      volume: 0,
      duration: dur,
      ease: 'audioEaseOut',
      overwrite: true,
      onComplete() {
        if (!playbackFile) return resolve(true);
        playbackFile.pause();
        const { currentTime } = playbackFile;

        // current time is not near beginning or end
        if (currentTime > dur && playbackFile.duration > currentTime) {
          // subtract tween duration from current time
          playbackFile.currentTime = currentTime - dur;
        }

        resolve(true);
      }
    });
  });
}

/**
 * Resumes paused audio. For use by unmute().
 *
 * Checks that there is an active audio element with `readyState` of 4.
 *
 * Because this function tweens the volume to 1 after playing, it first subtacts this tween's duration from `currentTime`, clipping at 0. If the `currentTime` results as 0, the tween is skipped.
 */
async function resume() {
  if (!isEnabled) {
    return false;
  }

  if (isSilent()) {
    return false;
  }

  // no active audio
  if (!activeId) {
    return false;
  }

  const dur = 0.5;

  // If play() was called while muted, it sets activeId but returns before
  // assigning playbackFile.src. On unmute the src is stale (previous audio or
  // empty) so resume() would fail the readyState check. Detect this by
  // comparing playbackFile.src to activeId and re-start playback from scratch.
  if (playbackFile.src !== activeId) {
    playbackFile.src = activeId;
    playbackFile.currentTime = 0;
    playbackFile.volume = 0;
    playbackFile.playbackRate = getPlaybackRate();
    try {
      await playbackFile.play();
      gsap.to(playbackFile, {
        volume: getVolume(),
        duration: dur,
        ease: 'expo.easeIn',
        overwrite: true
      });
    } catch (e) {
      console.error(e);
    }
    return true;
  }

  const { currentTime } = playbackFile;

  // already playing or audio failed to load
  if (
    !playbackFile.paused ||
    playbackFile.readyState !== HTMLMediaElement.HAVE_ENOUGH_DATA
  ) {
    return false;
  }

  if (currentTime > dur) {
    playbackFile.currentTime = currentTime - dur;

    try {
      playbackFile.play();

      gsap.to(playbackFile, {
        volume: getVolume(),
        duration: dur,
        ease: 'expo.easeIn',
        overwrite: true
      });
    } catch (e) {
      console.error(e);
    }
  } else {
    playbackFile.currentTime = 0;
    playbackFile.volume = getVolume();
    playbackFile.playbackRate = getPlaybackRate();
    playbackFile.play();
  }

  return true;
}

/**
 * Set global mute and all granular mute settings to true.
 */
function muteAll() {
  set('mute', true);

  mute();
  disableSfx();
  set('videoMute', true);
  set('captions', true);

  return true;
}

/**
 * Set global mute and all granular mute settings to false.
 */
function unmuteAll() {
  set('mute', false);

  unmute();
  enableSfx();
  set('videoMute', false);

  return true;
}

/**
 * Sets `narrationMute` to true and calls `pause()`.
 */
function mute() {
  // set mute
  document.body.classList.add('-is-muted');
  set('narrationMute', true);

  // pause any audio
  if (isEnabled) {
    pause();
  }

  return true;
}

/**
 * Sets `narrationMute` to false and calls `resume()`.
 *
 * Because the active audio updates while the course is muted, `unmute()` will play the audio that should be playing had the course not been muted.
 */
function unmute() {
  // unset mute
  document.body.classList.remove('-is-muted');
  set('narrationMute', false);

  // resume any audio
  if (isEnabled) {
    resume();
  }

  return true;
}

function toggle() {
  if (get('narrationMute')) {
    unmute();
  } else {
    mute();
  }
}

/**
 * Disables all audio in the course.
 *
 * Currently playing audio is stopped and all other `Audio` methods are disabled via a flag variable. Applies a class to the body that may be used to hide audio controls.
 */
function disable() {
  stop();
  document.body.classList.add('-audio-disabled');
  isEnabled = false;
}

/**
 * Undoes the changes by `disable()`. Unlike `unmute()`, does not play any audio that could have been playing.
 */
function enable() {
  document.body.classList.remove('-audio-disabled');
  isEnabled = true;
}

/**
 * Returns true if the user has muted the course or if the browser tab is
 * hidden.
 */
function isSilent() {
  // don't play in hidden browser tab
  const isHidden = 'hidden' in document && document.hidden;

  return get('mute') || get('narrationMute') || isHidden;
}

/**
 * Returns a promise that resolves after a given delay. By default, or given a delay of 0, it resolves immediately.
 */
async function sleep(delay = 0) {
  if (delay > 0) {
    await new Promise(r => gsap.delayedCall(delay, r));
  }

  return true;
}

export {
  play,
  stop,
  queue,
  pause,
  mute,
  unmute,
  muteAll,
  unmuteAll,
  toggle,
  disable,
  enable
};
